import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { Box, Button, useMediaQuery, useTheme } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'

import {
  initialtConsumerScansFilter,
  useConsumerScanNotCreatedEntitiesOptions,
  useConsumerScanProductTypeOptions,
  useConsumerScanScannersOptions,
  useConsumerScanStatusOptions,
  useDownloadNotRecognizedImpactsCsv,
  useFilter,
  useGetConsumerScans,
  usePagination,
  useRewardActivityChallengesOptions,
} from 'src/hooks'
import { countOffset, sortToObject } from 'src/common/utils'
import { ETranslationNs } from 'src/types'
import { Loader } from '../loader'
import { Filter } from '../filter'
import { HelperWrapper } from '../helper-wrapper'
import { PaginationButtons, PaginationContext } from '../pagination-buttons'
import { MobileBottomLayout } from '../mobile-bottom-layout'
import { ScanToEarnManagementTable } from './table'
import { FoundItemsCounter } from '../found-items-counter'
import { defaultConsumerScanSort, defaultLimit, defaultPage } from 'src/constants'
import { ConsumerScansInfoWithFilter } from './table/info-filtered.component'

export const ScanToEarnManagementComponent = () => {
  const [t] = useTranslation(ETranslationNs.ScanForm)

  const [searchParams] = useSearchParams()
  const isFilterOpened = searchParams.get('open')

  const [isScans, setIsScans] = useState(true)

  const [filter, setFilter] = useFilter({ initialState: initialtConsumerScansFilter })
  const [sort, setSort] = useState(defaultConsumerScanSort)

  const { page, setPage, rowsPerPage, setRowsPerPage } = usePagination(defaultPage, defaultLimit)

  const { data, isLoading } = useGetConsumerScans({
    ...filter,
    offset: countOffset(page, rowsPerPage),
    limit: rowsPerPage,
    ...sortToObject(sort),
  })

  const { mutateAsync: downloadCsv } = useDownloadNotRecognizedImpactsCsv()

  const theme = useTheme()
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))

  useEffect(() => {
    if (!isLoading && data) {
      if (data.data.length === 0) {
        setIsScans(false)
      }

      if (data.data.length > 0) {
        setIsScans(true)
      }
    }
  }, [isLoading, data])

  const statusSelectionOptions = useConsumerScanStatusOptions()
  const scannersSelectionOptions = useConsumerScanScannersOptions()
  const productTypeSelectionOptions = useConsumerScanProductTypeOptions()
  const notCreatedEntitiesOptions = useConsumerScanNotCreatedEntitiesOptions()
  const challengesSelectionOptions = useRewardActivityChallengesOptions()

  const paginationValues = {
    count: data?.count ?? 0,
    page: page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    isShown: !!data && data.count > rowsPerPage,
  }

  const handleDownloadCsv = async () => {
    await downloadCsv()
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Filter
        sort={sort}
        setSort={setSort}
        searchFieldText={t('search-input-placeholder.v')}
        filter={filter}
        setFilter={setFilter}
        selectionOptions={[
          statusSelectionOptions,
          scannersSelectionOptions,
          productTypeSelectionOptions,
          notCreatedEntitiesOptions,
          challengesSelectionOptions,
        ]}
        initialState={initialtConsumerScansFilter}
        isFilterOpened={isFilterOpened ? isFilterOpened === 'true' : true}
      />

      {!isLoading && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'start',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            <FoundItemsCounter count={data?.count} />
            <ConsumerScansInfoWithFilter
              countInfo={data?.unknownCount}
              filterName={'product_type'}
              defaultValue={['all']}
              setFilter={setFilter}
            />
          </Box>
          <Button
            onClick={handleDownloadCsv}
            size="small"
            variant="contained"
            startIcon={<DownloadIcon />}
          >
            Not Recognized Impacts
          </Button>
        </Box>
      )}

      {isLoading && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: '30px',
          }}
        >
          <Loader isStatic />
        </Box>
      )}

      {!isLoading && !isScans && (
        <Box sx={{ maxWidth: '500px', fontSize: '14px', mt: '8px' }}>
          <HelperWrapper t="scan-history-empty-text" />
        </Box>
      )}
      <PaginationContext.Provider value={paginationValues}>
        {!isLoading && isScans && (
          <ScanToEarnManagementTable data={data?.data} sort={sort} setSort={setSort} />
        )}
        {!isTablet && !isLoading && isScans && <PaginationButtons />}
        {isTablet && !isLoading && isScans && (
          <MobileBottomLayout>
            <PaginationButtons buttonsOnly />
          </MobileBottomLayout>
        )}
      </PaginationContext.Provider>
    </Box>
  )
}
